import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { distributeRangeAtom } from '@root/features/profile/distribute-drawer/atoms'
import {
  DISTRIBUTE_RANGES,
  distributeRangeId,
  type DistributeRange,
} from '@root/features/profile/distribute-drawer/model'

import type { DistributionProps } from './distribution'

export const DistributionTable = (props: DistributionProps) => {
  const [, setDistributeRange] = useAtom(distributeRangeAtom)

  const handleClick = (
    event: React.MouseEvent<HTMLDivElement>,
    range: DistributeRange,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    setDistributeRange(range)
  }

  return (
    <Root>
      {props.distribution.map((segment, index) => (
        <Row
          key={index}
          {...segment}
          onClick={(event: React.MouseEvent<HTMLDivElement>) =>
            handleClick(event, segment.range)
          }
        />
      ))}
    </Root>
  )
}

const Root = styled.div`
  color: var(--text-11);

  display: grid;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  height: 100%;
`

type RowProps = {
  range: DistributeRange
  color: string
  border: string
  count: string
  percentage: React.ReactNode
  percentageValue: number | undefined
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
}

const Row = (props: RowProps) => {
  const rangeLabel = useMemo(
    () =>
      DISTRIBUTE_RANGES.find(
        (range) =>
          distributeRangeId(range[0]) === distributeRangeId(props.range),
      )?.[1],
    [props.range],
  )

  return (
    <RowRoot onClick={props.onClick}>
      <Color $color={props.color} $border={props.border} />
      <Title>{rangeLabel}</Title>
      <Value
        $color={
          props.percentageValue && props.percentageValue > 0
            ? props.color
            : 'var(--text-11)'
        }
      >
        {props.count}
        {props.percentage ? <>&nbsp;{props.percentage}</> : ''}
      </Value>
    </RowRoot>
  )
}

const RowRoot = styled.div`
  border: 1px solid transparent;
  display: grid;
  grid-template-columns: auto 1fr auto;
  row-gap: 12px;
  column-gap: 6px;
  padding: 6px;
  align-items: center;

  margin-inline: -6px;
  border-radius: 6px;

  transition: all 0.125s ease-in-out;

  &:hover {
    background: var(--background-3);
  }
`
const Color = styled.div<{ $color: string; $border: string }>`
  background: ${({ $color }) => $color};

  border-radius: 50%;
  width: 12px;
  height: 12px;
`
const Title = styled.div``
const Value = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  text-align: right;
  justify-self: end;
`
